import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";

/**
 * Admin sign-in. On success, redirects to where the user came from (or /).
 *
 * Also the single sign-on page for other apps (Jarvis): they send the browser
 * here with `?returnUrl=<their URL>`. Once signed in, the backend checks that
 * URL against its allow-list and hands back the URL to go to, with the token on
 * it (see backend/src/service/sso.py).
 */
export default function SignInPage() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Signing in is only ever to reach admin/private content, so the Knowledge
  // Base — not the portfolio landing page — is the sensible default landing.
  const dest = location.state?.from || "/articles";
  const returnUrl = new URLSearchParams(location.search).get("returnUrl");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // One hand-off per page load: StrictMode runs effects twice in dev.
  const handoffStarted = useRef(false);

  // Already signed in → skip the form: back to the client app, or onward here.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (!returnUrl) {
      navigate(dest, { replace: true });
      return;
    }
    if (handoffStarted.current) return;
    handoffStarted.current = true;
    apiService
      .ssoHandoff(returnUrl)
      .then(({ redirect_url }) => window.location.replace(redirect_url))
      .catch((err) =>
        setError(
          err?.response?.data?.detail || "Could not return to the app you came from.",
        ),
      );
  }, [isLoading, isAuthenticated, returnUrl, dest, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      // The effect above takes over once the user is set.
      await login(username, password);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Sign-in failed. Check your credentials.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return null;

  if (isAuthenticated) {
    return (
      <div className="dark-page flex min-h-screen items-center justify-center px-4">
        <p className={`text-sm ${error ? "text-rose-400" : "text-slate-500"}`}>
          {error || "Signing you in…"}
        </p>
      </div>
    );
  }

  return (
    <div className="dark-page flex min-h-screen items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900/40 p-8"
      >
        <h1 className="mb-1 text-2xl font-bold text-slate-100">Knowledge Base</h1>
        <p className="mb-6 text-sm text-slate-500">Sign in to continue.</p>

        <label className="mb-1 block text-sm text-slate-400">Username</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          autoComplete="username"
          className="mb-4 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 outline-none focus:border-sky-500"
        />

        <label className="mb-1 block text-sm text-slate-400">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className="mb-4 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 outline-none focus:border-sky-500"
        />

        {error && <p className="mb-4 text-sm text-rose-400">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-lg bg-sky-600 px-4 py-2 font-medium text-white transition hover:bg-sky-500 disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
